// WishArchive — `/wishes/archive` route (per `docs/spec/wishlist/DESIGN.md §5.8`).
//
// Desktop: 2-column layout — filter rail on the left, table on the right.
// Mobile fallback (W34.0 polish 13.1.10): collapsible vertical timeline
// grouped by `completed_at` year, with the filter rail collapsed into a
// filter-chip group under the hero.
// Pagination: 20/page per spec; prev/next buttons + jump-to-page input.
//
// Data invariant (per DATA_MODEL §8.8 + DESIGN §5.8): only
// `status='completed' AND deleted_at IS NULL`. The backend's archive
// endpoint already filters; we just project.

import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, ChevronRight, Library } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHero } from "@/components/primitives/PageHero";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { EmptyState } from "@/components/primitives/EmptyState";
import { WishArchiveTable } from "@/components/wish/WishArchiveTable";
import { WishArchiveTimeline } from "@/components/wish/WishArchiveTimeline";
import {
  WishArchiveFilter,
  type WishArchiveFilterValue,
} from "@/components/wish/WishArchiveFilter";
import {
  listArchiveWishes,
  type WishCategory,
  type WishPriority,
  type WishSummary,
} from "@/lib/api/wishlist";
import { useIsMobile } from "@/lib/hooks/useIsMobile";
import { cn } from "@/lib/utils/cn";

const PAGE_SIZE = 20;

const DEFAULT_FILTER: WishArchiveFilterValue = {
  years: [],
  categories: [],
  priorities: [],
  completedFrom: "",
  completedTo: "",
};

export function WishArchivePage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [filter, setFilter] = useState<WishArchiveFilterValue>(DEFAULT_FILTER);
  const [offset, setOffset] = useState(0);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);

  const query = useQuery({
    queryKey: [
      "wishes",
      "archive",
      {
        years: filter.years,
        categories: filter.categories,
        priorities: filter.priorities,
        completedFrom: filter.completedFrom,
        completedTo: filter.completedTo,
        limit: PAGE_SIZE,
        offset,
      },
    ],
    queryFn: () =>
      listArchiveWishes({
        years: filter.years,
        category: filter.categories as WishCategory[] | undefined,
        priority: filter.priorities as WishPriority[] | undefined,
        completedFrom: filter.completedFrom || undefined,
        completedTo: filter.completedTo || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    staleTime: 30_000,
  });

  // Memoised so the `availableYears` memo below (dep: `[items]`) keeps a
  // stable identity across renders — `?? []` alone allocates a fresh array
  // every render and defeats the memo (react-hooks/exhaustive-deps).
  const items: WishSummary[] = useMemo(() => query.data?.items ?? [], [query.data]);
  const total: number = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  // Derive available years from the data when total > 0; otherwise fall
  // back to a 5-year window so the filter rail never feels empty.
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    for (const it of items) {
      if (it.completed_at) {
        const y = Number.parseInt(it.completed_at.slice(0, 4), 10);
        if (!Number.isNaN(y)) years.add(y);
      }
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [items]);

  const onPrev = useCallback(() => {
    setOffset((o) => Math.max(0, o - PAGE_SIZE));
  }, []);
  const onNext = useCallback(() => {
    setOffset((o) => (o + PAGE_SIZE < total ? o + PAGE_SIZE : o));
  }, [total]);

  return (
    <PageErrorBoundary label="心願 archive">
      {/* W49.0 §6 — hero outside `PageContainer`; see `Wishes.tsx`. */}
      <div className="flex flex-col" data-testid="wish-archive-page">
        {/* W52.0 D-9/D-11 — this page had **no** back affordance, and it was
            not on `probe-back-affordances.mjs`'s hand-written list, so the old
            sweep reported green. It is the twelfth page the D-9 convergence
            covers and the first one the new **coverage** mode found on its own
            run — which is the argument for deriving the list from `App.tsx`
            rather than keeping it by hand. */}
        <PageHero
          crumbs={[
            { to: "/wishes", label: "心願", testId: "wish-archive-back" },
            { label: "已實現" },
          ]}
          title="心願 archive"
          eyebrow="已實現"
          subtitle={`${total} 個已實現心願`}
        />
        <PageContainer>
          <div
            data-component="WishArchiveLayout"
            data-viewport={isMobile ? "mobile" : "desktop"}
            className={cn(
              "gap-s-3",
              isMobile ? "flex flex-col" : "grid grid-cols-1 lg:grid-cols-[280px_1fr]",
            )}
          >
            {isMobile ? (
              <MobileFilterBar
                filterOpen={filterOpen}
                onToggle={() => setFilterOpen((v) => !v)}
                total={total}
              />
            ) : (
              <WishArchiveFilter
                value={filter}
                onChange={(next) => {
                  setFilter(next);
                  setOffset(0);
                }}
                availableYears={availableYears}
              />
            )}
            {isMobile && filterOpen ? (
              <WishArchiveFilter
                value={filter}
                onChange={(next) => {
                  setFilter(next);
                  setOffset(0);
                }}
                availableYears={availableYears}
              />
            ) : null}
            <section className="flex flex-col gap-s-3">
              {query.isLoading ? (
                <LoadingState shape="card" count={3} />
              ) : query.isError ? (
                <ErrorState
                  title="讀取 archive 失敗"
                  description={query.error instanceof Error ? query.error.message : "請稍後再試"}
                  onRetry={() => query.refetch()}
                />
              ) : total === 0 ? (
                <EmptyState
                  title="還沒有已實現的心願"
                  description="完成 N 個心願後，會在這裡以紀念視角呈現。"
                  actionLabel="回到心願板"
                  onAction={() => navigate("/wishes")}
                  icon={<Library className="h-8 w-8 text-ink-3" />}
                />
              ) : isMobile ? (
                <>
                  <WishArchiveTimeline items={items} />
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    total={total}
                    onPrev={onPrev}
                    onNext={onNext}
                    onGoTo={(page) => setOffset((page - 1) * PAGE_SIZE)}
                  />
                </>
              ) : (
                <>
                  <WishArchiveTable items={items} />
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    total={total}
                    onPrev={onPrev}
                    onNext={onNext}
                    onGoTo={(page) => setOffset((page - 1) * PAGE_SIZE)}
                  />
                </>
              )}
            </section>
          </div>
        </PageContainer>
      </div>
    </PageErrorBoundary>
  );
}

function Pagination({
  currentPage,
  totalPages,
  total,
  onPrev,
  onNext,
  onGoTo,
}: {
  currentPage: number;
  totalPages: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  onGoTo: (page: number) => void;
}) {
  const [jumpInput, setJumpInput] = useState<string>("");
  return (
    <nav
      data-component="WishArchivePagination"
      data-testid="wish-archive-pagination"
      className="flex items-center justify-between gap-s-3 px-s-3"
    >
      <span className="text-meta text-ink-3 tabular-nums">
        第 {currentPage} / {totalPages} 頁 · 共 {total} 筆
      </span>
      <div className="flex items-center gap-s-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={currentPage <= 1}
          data-testid="wish-archive-pagination-prev"
          className="press min-h-11 inline-flex items-center gap-1.5 px-s-3 rounded-full border border-hairline text-ink-1 disabled:opacity-50"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          上一頁
        </button>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const target = Math.max(1, Math.min(totalPages, Number.parseInt(jumpInput, 10) || 1));
            onGoTo(target);
            setJumpInput("");
          }}
          className="flex items-center gap-s-1"
        >
          <label className="text-meta text-ink-3">跳至</label>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={jumpInput}
            onChange={(e) => setJumpInput(e.target.value)}
            data-testid="wish-archive-pagination-jump"
            className={cn(
              "w-16 min-h-11 px-2 rounded-md border border-hairline bg-paper-elevated text-ink-1 text-center",
            )}
          />
          <button
            type="submit"
            data-testid="wish-archive-pagination-go"
            // `min-w-11` for the same reason as `wish-new-mobile-back`: two CJK
            // glyphs plus padding measure 42.5px, and `min-h-11` alone only
            // guarantees one axis (W46.0).
            className="press min-h-11 min-w-11 px-s-3 rounded-md text-meta text-ink-1 hover:bg-paper-deep"
          >
            前往
          </button>
        </form>
        <button
          type="button"
          onClick={onNext}
          disabled={currentPage >= totalPages}
          data-testid="wish-archive-pagination-next"
          className="press min-h-11 inline-flex items-center gap-1.5 px-s-3 rounded-full border border-hairline text-ink-1 disabled:opacity-50"
        >
          下一頁
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </nav>
  );
}

// --- Mobile filter bar (W34.0 polish 13.1.10) ---------------------------

interface MobileFilterBarProps {
  filterOpen: boolean;
  onToggle: () => void;
  total: number;
}

function MobileFilterBar({ filterOpen, onToggle, total }: MobileFilterBarProps) {
  return (
    <div
      data-component="WishArchiveMobileFilterBar"
      className="bg-paper-elevated border border-hairline rounded-2xl px-s-4 py-s-3 flex items-center justify-between gap-s-2"
    >
      <span className="text-meta text-ink-2 tabular-nums">共 {total} 筆 · 依「完成於」排序</span>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={filterOpen}
        data-testid="wish-archive-mobile-filter-toggle"
        className="press inline-flex items-center gap-1.5 min-h-11 px-s-3 rounded-full border border-hairline text-ink-1"
      >
        篩選
        {filterOpen ? (
          <ChevronDown className="h-4 w-4 rotate-180 transition-transform" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 transition-transform" aria-hidden />
        )}
      </button>
    </div>
  );
}
